import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Building2, User, Check, ChevronDown, Plus, Shield } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { useAuth } from "@/stores/auth/auth-store";
import { OrganizationCreationWizard } from "./OrganizationCreationWizard";
import { cn } from "@/lib/utils";

/**
 * Organization Context Switcher
 *
 * Allows users to switch between personal (user) context and organization contexts.
 * Displays current context and provides dropdown to switch.
 *
 * Platform Admin Mode: When user has TerraMatrix platform-admin membership,
 * they can see and switch to ANY organization in the system.
 *
 * Phase 1: Dual Ownership Model Component
 */
export const OrganizationContextSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const {
    organizations,
    allOrganizations,
    currentContext,
    fetchOrganizations,
    fetchAllOrganizations,
    switchToUserContext,
    switchToOrgContext,
    initialize,
    isPlatformAdmin,
    platformAdminEntitlement,
    initialized,
  } = useOrganizationStore();
  const [showCreateWizard, setShowCreateWizard] = useState(false);

  const getContextlessPath = (pathname: string) => {
    const scopedMatch = pathname.match(/^\/(?:orgs\/[^/]+|users\/[^/]+)(\/.*)$/);
    return scopedMatch ? scopedMatch[1] : pathname;
  };

  const isEditorOrViewerPath = (pathname: string) =>
    /^\/(?:editor|viewer|view)\/[^/]+(?:\/page\/[^/]+)?$/.test(pathname);

  // Initialize store and fetch organizations on mount
  useEffect(() => {
    initialize();
    if (user) {
      fetchOrganizations().catch(() => {
        // Silent fail - UI shows empty state
      });
    }
  }, [user, initialize, fetchOrganizations]);

  // Fetch all organizations when the user is entitled to platform-admin access.
  useEffect(() => {
    if (platformAdminEntitlement && allOrganizations.length === 0) {
      fetchAllOrganizations().catch(() => {
        // Silent fail - UI shows empty state
      });
    }
  }, [platformAdminEntitlement, allOrganizations.length, fetchAllOrganizations]);

  // Set default context to user if none is set (only after store is initialized)
  useEffect(() => {
    // Wait for store to initialize first - it will load persisted context from localStorage
    if (initialized && user && !currentContext) {
      switchToUserContext(user.username);
    }
  }, [initialized, user, currentContext, switchToUserContext]);

  const handleSwitchToUser = () => {
    if (user) {
      switchToUserContext(user.username);

      // Navigate away from org-scoped routes to personal routes
      const currentPath = location.pathname;
      const contextlessPath = getContextlessPath(currentPath);

      // If currently on an org-scoped page, navigate to the personal equivalent
      if (currentPath.startsWith("/orgs/")) {
        if (currentPath.includes("/projects")) {
          navigate("/projects");
        } else if (currentPath.includes("/dashboard")) {
          navigate("/dashboard");
        } else if (isEditorOrViewerPath(contextlessPath)) {
          navigate(contextlessPath);
        } else {
          // Default: go to personal dashboard for other org routes
          navigate("/dashboard");
        }
      }
      // Otherwise stay on current page (e.g., /editor, /viewer - not org-scoped)
    }
  };

  const handleSwitchToOrg = (orgSlug: string) => {
    console.log("[ContextSwitcher] handleSwitchToOrg called with:", orgSlug);
    console.log("[ContextSwitcher] Current path:", location.pathname);

    switchToOrgContext(orgSlug);

    // Navigate to the new org's equivalent page
    const currentPath = location.pathname;
    const contextlessPath = getContextlessPath(currentPath);

    // If currently on an org-scoped page, navigate to the equivalent page for the new org
    const orgRouteMatch = currentPath.match(/^\/orgs\/[^/]+(\/.*)$/);
    console.log("[ContextSwitcher] orgRouteMatch:", orgRouteMatch);

    if (orgRouteMatch) {
      // Currently on /orgs/old-slug/something, navigate to /orgs/new-slug/something
      const subPath = orgRouteMatch[1]; // e.g., "/projects" or "/settings"
      const newPath = `/orgs/${orgSlug}${subPath}`;
      console.log("[ContextSwitcher] Navigating to:", newPath);
      navigate(newPath);
    } else if (currentPath === "/projects" || currentPath === "/dashboard") {
      // On personal projects/dashboard, navigate to org's projects page
      const newPath = `/orgs/${orgSlug}/projects`;
      console.log("[ContextSwitcher] Navigating to:", newPath);
      navigate(newPath);
    } else if (isEditorOrViewerPath(contextlessPath)) {
      const newPath = `/orgs/${orgSlug}${contextlessPath}`;
      console.log("[ContextSwitcher] Navigating to:", newPath);
      navigate(newPath);
    } else {
      console.log("[ContextSwitcher] No navigation - staying on current page");
    }
    // Otherwise stay on current page (e.g., /editor, /viewer - not org-scoped)
  };

  const handleCreateSuccess = (orgSlug: string) => {
    switchToOrgContext(orgSlug);
    fetchOrganizations();
  };

  // Don't render if user is not logged in
  if (!user) {
    return null;
  }

  const isUserContext = currentContext?.type === "user";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-md transition-colors",
            "border border-border bg-background hover:bg-accent",
            "focus:outline-none focus:ring-2 focus:ring-ring",
            isPlatformAdmin && "border-amber-500/50"
          )}
          aria-label="Switch organization context"
        >
          {isPlatformAdmin && (
            <Shield className="h-4 w-4 text-amber-500" />
          )}
          {isUserContext ? (
            <User className="h-4 w-4" />
          ) : (
            <Building2 className="h-4 w-4" />
          )}
          <span className="hidden sm:inline text-sm font-medium truncate max-w-[150px]" title={currentContext?.displayName || user.username}>
            {currentContext?.displayName || user.username}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 max-h-96 overflow-y-auto">
        {/* Platform Admin Badge */}
        {isPlatformAdmin && (
          <>
            <div className="px-2 py-2 bg-amber-500/10 rounded-t-md">
              <div className="flex items-center gap-2 text-amber-600">
                <Shield className="h-4 w-4" />
                <span className="text-xs font-semibold">Platform Admin Mode</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Granted by TerraMatrix membership
              </p>
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Switch Context
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Personal Context */}
        <DropdownMenuItem
          onClick={handleSwitchToUser}
          className="flex items-center gap-2"
        >
          <User className="h-4 w-4" />
          <span className="flex-1">{user.username} (Personal)</span>
          {isUserContext && <Check className="h-4 w-4" />}
        </DropdownMenuItem>

        {/* My Organizations (memberships) */}
        {organizations.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              My Organizations
            </DropdownMenuLabel>
            {organizations.map((org) => {
              const isCurrentOrg =
                currentContext?.type === "organization" &&
                currentContext.orgSlug === org.slug;

              return (
                <DropdownMenuItem
                  key={org._id}
                  onClick={() => handleSwitchToOrg(org.slug)}
                  className="flex items-center gap-2"
                >
                  <Building2 className="h-4 w-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" title={org.name}>{org.name}</div>
                    {org.membership && (
                      <div className="text-xs text-muted-foreground">
                        {org.membership.role}
                      </div>
                    )}
                  </div>
                  {isCurrentOrg && <Check className="h-4 w-4" />}
                </DropdownMenuItem>
              );
            })}
          </>
        )}

        {/* All Organizations (platform admin only) */}
        {platformAdminEntitlement && allOrganizations.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-amber-600 flex items-center gap-1">
              <Shield className="h-3 w-3" />
              All Organizations ({allOrganizations.length})
            </DropdownMenuLabel>
            {allOrganizations
              .filter(org => !organizations.some(myOrg => myOrg._id === org._id))
              .map((org) => {
                const isCurrentOrg =
                  currentContext?.type === "organization" &&
                  currentContext.orgSlug === org.slug;

                return (
                  <DropdownMenuItem
                    key={org._id}
                    onClick={() => handleSwitchToOrg(org.slug)}
                    className="flex items-center gap-2"
                  >
                    <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate" title={org.name}>{org.name}</div>
                      <div className="text-xs text-muted-foreground truncate" title={org.slug}>
                        {org.slug}
                      </div>
                    </div>
                    {isCurrentOrg && <Check className="h-4 w-4" />}
                  </DropdownMenuItem>
                );
              })}
          </>
        )}

        {organizations.length === 0 && !platformAdminEntitlement && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-3 text-sm text-muted-foreground text-center">
              No organizations yet
            </div>
          </>
        )}

        {/* Create Organization Button */}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => setShowCreateWizard(true)}
          className="flex items-center gap-2 text-primary"
        >
          <Plus className="h-4 w-4" />
          <span className="font-medium">Create Organization</span>
        </DropdownMenuItem>
      </DropdownMenuContent>

      {/* Organization Creation Wizard */}
      <OrganizationCreationWizard
        open={showCreateWizard}
        onOpenChange={setShowCreateWizard}
        onSuccess={handleCreateSuccess}
      />
    </DropdownMenu>
  );
};
